import { INTEGRATIONS_TYPES } from 'config/integrationTypes';
import { SOCKET_EVENTS } from 'config/socketEvents';
import { forceRefreshToken, getAuthToken } from 'library/auth/authProvider';
import socketURLGenerator from 'library/helpers/socketURLGenerator';
import debounce from 'lodash/debounce';
import { createAccessRefreshGate } from './accessRefreshGate';
import { createPropagationRefresher } from './propagationRefresh';
import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { accessActions } from 'reduxStore/access/slice';
import { accessWindowsActions } from 'reduxStore/accessWindows/slice';
import { accessLogsActions } from 'reduxStore/accessLogs/slice';
import { lockerLogWindowRequest } from 'library/helpers/lockerHelpers';
import { appActions } from 'reduxStore/app/slice';
import { authActions } from 'reduxStore/auth/slice';
import { assetGroupActions } from 'reduxStore/assetGroups/slice';
import { bookingActions } from 'reduxStore/bookings/slice';
import { categoriesActions } from 'reduxStore/categories/slice';
import { credentialsActions } from 'reduxStore/credentials/slice';
import { devicesActions } from 'reduxStore/devices/slice';
import { featureFlagActions } from 'reduxStore/featureFlags/slice';
import { notificationsActions } from 'reduxStore/notifications/slice';
import { peopleActions } from 'reduxStore/people/slice';
import { settingsActions } from 'reduxStore/settings/slice';
import { typesActions } from 'reduxStore/types/slice';
import { io } from 'socket.io-client';
import { Notification } from 'zComponents/atoms';
import { createSyncWatchdog } from './syncWatchdog';

const SocketManager = () => {
	const dispatch = useDispatch();
	const permissions = useSelector((state) => state.Auth.userPermissions);
	const { fBPersonID, personID, orgID, roles } = useSelector((state) => state.Auth.selectedProfile);

	// 1. Create a safe, primitive dependency string.
	// This prevents Redux array mutations from unnecessarily restarting the socket.
	const permissionsKey = permissions
		.map((p) => p.orgID)
		.sort()
		.join(',');

	// Stable key for site rooms derived from role siteIDs
	const siteRoomsKey = Object.values(roles || {})
		.flat()
		.filter((v) => typeof v === 'number')
		.sort((a, b) => a - b)
		.join(',');

	// 2. Keep orgID and personID in Refs.
	// This allows event listeners to always access the latest values without restarting the connection.
	const orgIdRef = useRef(orgID);
	const personIdRef = useRef(personID);
	useEffect(() => {
		orgIdRef.current = orgID;
		personIdRef.current = personID;
	}, [orgID, personID]);

	// An optimistic reschedule is in flight: a BOOKING_CHANGED reload now would snap the block back.
	// The reschedule saga reloads when the drag settles, so nothing skipped here is lost.
	const rescheduleInFlight = useSelector((state) => Object.keys(state.Bookings.rescheduleSnapshots || {}).length > 0);
	// Which dashboard is on screen — read through a ref so the debounced refresh
	// below sees the CURRENT value without being re-created on every switch.
	const selectedDashboard = useSelector((state) => state.App.dashboard.selected);
	const isLockersDashboardRef = useRef(false);
	useEffect(() => {
		isLockersDashboardRef.current = selectedDashboard === 'lockers';
	}, [selectedDashboard]);

	const dragActiveRef = useRef(false);
	useEffect(() => {
		dragActiveRef.current = rescheduleInFlight;
	}, [rescheduleInFlight]);

	useEffect(() => {
		// Connect for any org member: org/site rooms need no person id, so a Kinde-only user with no
		// Firebase uid still gets org/site events.
		if (!permissionsKey) return;

		// Rooms come from the permissionsKey string so the permissions array isn't an effect dependency.
		const rooms = permissionsKey
			.split(',')
			.filter(Boolean)
			.map((id) => `org:${id}`);

		// Add site-scoped rooms so site admins receive per-site events
		if (siteRoomsKey) {
			siteRoomsKey
				.split(',')
				.filter(Boolean)
				.forEach((siteID) => {
					rooms.push(`site:${siteID}`);
				});
		}

		// person:<fbUID> (legacy) plus person:<personID>, which is how Kinde sessions get person events.
		// The server emits to the union once; notifications also de-dupe by notificationID.
		if (fBPersonID) rooms.push(`person:${fBPersonID}`);
		if (personID != null) rooms.push(`person:${personID}`);

		const payload = { rooms };

		// The app's single socket. `auth` is a function so every attempt, reconnects included, sends a
		// fresh token; a failed fetch connects tokenless and the server decides (SOCKET_AUTH_REQUIRED).
		const socket = io(socketURLGenerator(), {
			query: { data: JSON.stringify(payload) },
			auth: (cb) => {
				// 🛑 cb must always run or the namespace never joins; the timeout covers a token fetch
				// that hangs (auth failing to initialise).
				let settled = false;
				const send = (token) => {
					if (!settled) {
						settled = true;
						cb(token ? { token } : {});
					}
				};
				setTimeout(() => send(undefined), 10000);
				// Provider-aware bearer (Kinde access token or Firebase id token) so the
				// server (helpers/socketRoomAuth) can verify + entitle either identity.
				getAuthToken()
					.then(send)
					.catch(() => send(undefined));
			}
		});

		// Drop an event only when it carries a different orgID; events without one still fire
		// (some payloads don't include it yet).
		const isForCurrentOrg = (data) => data?.orgID == null || String(data.orgID) === String(orgIdRef.current);

		// A middleware rejection destroys the socket (no auto-reconnect), so retry by hand with a fresh
		// token; backoff doubles up to 60s and resets on connect.
		let authRetryTimer;
		let authRetryDelay = 5000;
		let hasConnected = false;
		socket.on('connect', () => {
			authRetryDelay = 5000;
			// Signals sent while disconnected are lost: a reconnect refreshes the propagation snapshot.
			if (hasConnected) propagationRefresher.onChanged();
			hasConnected = true;
		});
		socket.on('connect_error', () => {
			if (!socket.active) {
				// The token cache TTL equals the first retry delay, so force a fresh token or the retry
				// would reuse the rejected one.
				forceRefreshToken();
				clearTimeout(authRetryTimer);
				authRetryTimer = setTimeout(() => socket.connect(), authRetryDelay);
				authRetryDelay = Math.min(authRetryDelay * 2, 60000);
			}
		});

		// Silent GET /access for GRANTED/REVOKED_PERMISSION only; the access saga coalesces bursts with the
		// save sagas' own reloads, and a hidden tab waits until it is visible (accessRefreshGate.js).
		// Never wire it to propagation events: org-wide /access is heavy and those fire for hours on retries.
		const accessRefreshGate = createAccessRefreshGate({
			refresh: () => dispatch(accessActions.refreshAccessPermissions())
		});
		document.addEventListener('visibilitychange', accessRefreshGate.onVisibilityChange);

		// CHANGED fires on every delivery transition; it refreshes only the propagation snapshot (policy in
		// propagationRefresh.js). Delivery state never changes permission rows, so /access is not reloaded.
		const propagationRefresher = createPropagationRefresher({
			refresh: () => dispatch(credentialsActions.fetchPropagation())
		});
		document.addEventListener('visibilitychange', propagationRefresher.onVisibilityChange);

		// A booking changed elsewhere: refresh the calendar + approvals queue, one refetch per burst
		// (a recurring series, quick edits).
		const refreshRoomBookings = debounce(
			() => {
				// The pending queue has no optimistic state — always safe to refresh.
				dispatch(bookingActions.loadPendingBookings());
				// Skipped (checked at fire time) during an optimistic drag, or the replace would revert it;
				// the reschedule saga reloads afterwards.
				if (dragActiveRef.current) return;
				dispatch(bookingActions.loadRoomBookings());
			},
			600,
			{ maxWait: 2500 }
		);

		// Every unlock emits LOG_ACCESS_EVENT and a locker bank bursts; coalesce into one trailing
		// log refetch.
		const refreshLogs = debounce(
			() => {
				dispatch(accessLogsActions.loadAccessLogs({ orgID: orgIdRef.current }));
				dispatch(accessLogsActions.loadTodayCounts());
				// 90 days, paged through in full even with the deviceIDs filter, so refresh it
				// only while that dashboard is on screen.
				if (isLockersDashboardRef.current) {
					dispatch(accessLogsActions.loadLogsWindow(lockerLogWindowRequest({ force: true })));
				}
			},
			1500,
			{ maxWait: 5000 }
		);

		// ── Event registration (bound once) ──
		socket.on(SOCKET_EVENTS.ADD_DEVICE_EVENT, (data) => {
			if (isForCurrentOrg(data)) {
				Notification('success', 'New device has been added');
			}
		});

		// A dev flipped a feature flag. Re-hydrate on a change to THIS org's override, or a
		// platform-default broadcast (orgID null → affects every org). Silent (no toast).
		socket.on(SOCKET_EVENTS.FEATURE_FLAG_CHANGED, (data) => {
			if (isForCurrentOrg(data)) {
				dispatch(featureFlagActions.loadFeatureFlags());
			}
		});

		// An org setting changed (integration syncing flags): patch it so the "syncing…" indicator
		// follows start/finish live. Silent.
		socket.on(SOCKET_EVENTS.ORG_SETTING_UPDATED, (data) => {
			if (data?.orgID === orgIdRef.current) {
				dispatch(settingsActions.updateOrgSettingValue(data));
			}
		});

		socket.on(SOCKET_EVENTS.GRANTED_PERMISSION, (data) => {
			if (!isForCurrentOrg(data)) return;

			// A personID targets one person: act only if it's this user; org-wide events (no personID) always.
			const isEventForSpecificPerson = data.personID != null;
			const shouldProcessEvent =
				!isEventForSpecificPerson || Number(data.personID) === Number(personIdRef.current);

			if (shouldProcessEvent) {
				// Permission rows changed: refresh the Access slice.
				accessRefreshGate.onSignal();
				// Fixed key: a burst of grants (one emit per call) shows one toast, not a stack.
				Notification('success', 'Access Granted', undefined, undefined, 'socket-access-granted');
			}
		});

		socket.on(SOCKET_EVENTS.REVOKED_PERMISSION, (data) => {
			if (!isForCurrentOrg(data)) return;

			// A personID targets one person: act only if it's this user; org-wide events (no personID) always.
			const isEventForSpecificPerson = data.personID != null;
			const shouldProcessEvent =
				!isEventForSpecificPerson || Number(data.personID) === Number(personIdRef.current);

			if (shouldProcessEvent) {
				accessRefreshGate.onSignal();
				Notification('success', 'Access Revoked', undefined, undefined, 'socket-access-revoked');
			}
		});

		// Credential propagation: CHANGED ({ orgID }) only says "state moved"; refetch the snapshot
		// (debounced), which is the single source of truth.
		socket.on(SOCKET_EVENTS.CREDENTIAL_PROPAGATION_CHANGED, (data) => {
			if (data?.orgID === orgIdRef.current) {
				propagationRefresher.onChanged();
			}
		});

		socket.on(SOCKET_EVENTS.SETTING_ENABLE_ACCESS_CONTROL, (data) => {
			if (isForCurrentOrg(data)) {
				dispatch(settingsActions.loadOrgSettings());
				Notification('success', 'Authorization granted');
			}
		});

		socket.on(SOCKET_EVENTS.SETTING_DISABLE_ACCESS_CONTROL, (data) => {
			if (isForCurrentOrg(data)) {
				dispatch(settingsActions.loadOrgSettings());
				Notification('success', 'Authorization revoked');
			}
		});

		socket.on(SOCKET_EVENTS.LOG_ACCESS_EVENT, () => {
			refreshLogs();
		});

		socket.on(SOCKET_EVENTS.LOG_SYNC_COMPLETE, () => {
			dispatch(accessLogsActions.loadAccessLogs({ orgID: orgIdRef.current }));
			dispatch(accessLogsActions.loadTodayCounts());
		});

		// New notification for this user (sent to their person room, so no org guard): prepend it and
		// bump the badges.
		socket.on(SOCKET_EVENTS.NOTIFICATION_NEW, (data) => {
			if (data && data.notificationID != null) {
				dispatch(notificationsActions.notificationReceived(data));
			}
		});

		// An existing row changed state elsewhere (another tab/device) — re-sync the badges.
		socket.on(SOCKET_EVENTS.ALERT_UPDATED, () => {
			dispatch(notificationsActions.loadCounts());
		});

		// Room/space booking changed on the server → live-refresh the calendar + approvals (silent; the
		// calendar just updates). Org-scoped guard; coalesced via refreshRoomBookings.
		socket.on(SOCKET_EVENTS.BOOKING_CHANGED, (data) => {
			if (isForCurrentOrg(data)) refreshRoomBookings();
		});

		// --- ORG-SPECIFIC EVENTS ---
		socket.on(SOCKET_EVENTS.LOCKER_BOOKING_CREATED, (data) => {
			if (data?.orgID === orgIdRef.current) {
				dispatch(bookingActions.loadBookings());
				Notification('success', 'New booking created');
			}
		});

		socket.on(SOCKET_EVENTS.LOCKER_BOOKED_BY_PERSON, (data) => {
			if (data?.orgID === orgIdRef.current) {
				dispatch(bookingActions.loadBookings());
				Notification('success', 'New booking created');
			}
		});

		socket.on(SOCKET_EVENTS.LOCKER_BOOKING_DELETED, (data) => {
			if (data?.orgID === orgIdRef.current) {
				dispatch(bookingActions.loadBookings());
				Notification('success', 'Booking deleted');
			}
		});

		socket.on(SOCKET_EVENTS.LOCKER_RELEASED_BY_PERSON, (data) => {
			if (data?.orgID === orgIdRef.current) {
				dispatch(bookingActions.loadBookings());
				Notification('success', 'Booking released');
			}
		});

		// --- CRITICAL SYNC EVENTS ---

		// A lost terminal event would spin the sync indicator forever: on timeout drop the client step
		// and re-read org settings, so a sync still running keeps its indicator (helpers/syncWatchdog.js).
		const syncWatchdog = createSyncWatchdog({
			onTimeout: () => {
				dispatch(appActions.clearSyncStep());
				dispatch(settingsActions.loadOrgSettings());
			}
		});

		socket.on(SOCKET_EVENTS.SYNC_PROGRESS, (data) => {
			if (data?.orgID === orgIdRef.current) {
				dispatch(appActions.setSyncStep(data.label));
				// Re-armed per step, so the timeout bounds the gap BETWEEN progress events, never the
				// sync as a whole — a long sync that keeps reporting is never interrupted.
				syncWatchdog.arm();
			}
		});

		// Post-sync refresh per integration: one shared list for the full IR/Inception syncs so they can't
		// drift; Control/TTLock refresh devices and cards only.
		const fullSyncRefresh = () => {
			dispatch(categoriesActions.loadCategories());
			dispatch(typesActions.loadCardTemplatesTypes());
			dispatch(credentialsActions.loadCredentials());
			dispatch(assetGroupActions.loadGroups());
			dispatch(devicesActions.loadDevices());
			dispatch(peopleActions.loadPerson());
			// A full sync rewrites permissions too; refresh the Access slice behind the drawers' Access tabs.
			dispatch(accessActions.getAccessPermissions());
			// The TimePeriod sync creates/updates schedules; reload them so permissions show names, not
			// a bare "Scheduled".
			dispatch(accessWindowsActions.loadAccessWindows());
		};
		const SYNC_COMPLETE_HANDLERS = {
			[INTEGRATIONS_TYPES.INTEGRATION_INNER_RANGE.id]: {
				refresh: () => {
					fullSyncRefresh();
					// IR only: the TimePeriods still unlinked feed 'Link IR TimePeriod'.
					dispatch(accessWindowsActions.loadExternalTimePeriods());
				},
				label: 'Integriti'
			},
			[INTEGRATIONS_TYPES.INTEGRATION_INCEPTION.id]: { refresh: fullSyncRefresh, label: 'Inception' },
			[INTEGRATIONS_TYPES.INTEGRATION_CONTROL.id]: {
				refresh: () => {
					dispatch(categoriesActions.loadCategories());
					dispatch(devicesActions.loadDevices());
					dispatch(credentialsActions.loadCredentials());
				},
				label: 'Zezamii Lock'
			},
			// TTLock syncs the same shapes as Control: devices + credentials
			[INTEGRATIONS_TYPES.INTEGRATION_TTLOCK.id]: {
				refresh: () => {
					dispatch(categoriesActions.loadCategories());
					dispatch(devicesActions.loadDevices());
					dispatch(credentialsActions.loadCredentials());
				},
				label: 'TTLock'
			}
		};

		socket.on(SOCKET_EVENTS.SYNC_COMPLETE, (data) => {
			if (data?.orgID === orgIdRef.current) {
				syncWatchdog.disarm();
				dispatch(appActions.clearSyncStep());
				dispatch(settingsActions.loadOrgSettings());

				const handler = SYNC_COMPLETE_HANDLERS[data?.integrationID];
				if (handler) {
					handler.refresh();
					Notification('success', `${handler.label} sync completed successfully.`);
				}
			}
		});

		socket.on(SOCKET_EVENTS.SYNC_ERROR, (data) => {
			if (data?.orgID === orgIdRef.current) {
				syncWatchdog.disarm();
				dispatch(appActions.clearSyncStep());
				dispatch(settingsActions.loadOrgSettings());

				const handler = SYNC_COMPLETE_HANDLERS[data?.integrationID];
				Notification(
					'error',
					handler ? `${handler.label} sync failed. Please try again.` : 'Sync failed. Please try again.'
				);
			}
		});

		// --- BULK CREDENTIAL CREATION EVENTS ---
		socket.on(SOCKET_EVENTS.CREDENTIAL_BULK_PROGRESS, (data) => {
			if (data?.orgID === orgIdRef.current) {
				dispatch(
					appActions.setBulkCredentialProgress({
						processed: data.processed,
						total: data.total,
						lastCard: data.lastCard || null
					})
				);
			}
		});

		socket.on(SOCKET_EVENTS.CREDENTIAL_BULK_COMPLETE, (data) => {
			if (data?.orgID === orgIdRef.current) {
				dispatch(
					appActions.setBulkCredentialProgress({
						complete: true,
						total: data.total,
						succeeded: data.succeeded,
						failed: data.failed || []
					})
				);
				dispatch(credentialsActions.loadCredentials());
			}
		});

		socket.on(SOCKET_EVENTS.CREDENTIAL_BULK_ERROR, (data) => {
			if (data?.orgID === orgIdRef.current) {
				dispatch(appActions.clearBulkCredentialProgress());
			}
		});

		// ── Person events (site-scoped) ──
		socket.on(SOCKET_EVENTS.PERSON_ADDED, (data) => {
			if (data?.orgID === orgIdRef.current) {
				dispatch(peopleActions.refreshPeople());
			}
		});

		socket.on(SOCKET_EVENTS.PERSON_UPDATED, (data) => {
			if (data?.orgID === orgIdRef.current) {
				dispatch(peopleActions.refreshPeople());
			}
		});

		socket.on(SOCKET_EVENTS.PERSON_DELETED, (data) => {
			if (data?.orgID === orgIdRef.current) {
				dispatch(peopleActions.refreshPeople());
			}
		});

		const isSignedInPerson = (data) =>
			data?.personID != null && String(data.personID) === String(personIdRef.current);

		// Re-enabled: reload, since every store failed (403) while locked and needs a clean boot.
		socket.on(SOCKET_EVENTS.PERSON_ENABLED, (data) => {
			if (data?.orgID != null && String(data.orgID) === String(orgIdRef.current)) {
				if (isSignedInPerson(data)) window.location.reload();
				else dispatch(peopleActions.refreshPeople());
			}
		});

		// Disabled (even by myself): re-read my profile, whose 403 locks the shell on AccountDisabled.
		socket.on(SOCKET_EVENTS.PERSON_DISABLED, (data) => {
			if (data?.orgID != null && String(data.orgID) === String(orgIdRef.current)) {
				if (isSignedInPerson(data)) dispatch(authActions.getUserProfile());
				else dispatch(peopleActions.refreshPeople());
			}
		});

		// Cleanup: cancel pending refetches and disconnect on logout/unmount.
		return () => {
			propagationRefresher.cancel();
			document.removeEventListener('visibilitychange', propagationRefresher.onVisibilityChange);
			refreshRoomBookings.cancel();
			refreshLogs.cancel();
			accessRefreshGate.cancel();
			document.removeEventListener('visibilitychange', accessRefreshGate.onVisibilityChange);
			clearTimeout(authRetryTimer);
			syncWatchdog.disarm();
			socket.disconnect();
		};
	}, [permissionsKey, siteRoomsKey, fBPersonID, personID, dispatch]);

	return <div />;
};

export default SocketManager;
